import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { apiCall } from '../utils/api';
import { useToast } from '../hooks/use-toast';
import { 
  Search, FileAudio, Calendar, MapPin, AlertTriangle, Eye, RefreshCw, 
  Filter, ChevronLeft, ChevronRight, Volume2, VolumeX, X
} from 'lucide-react';

const TranscriptionArchive = ({ onClose }) => {
  const [transcriptions, setTranscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRisk, setFilterRisk] = useState('all');
  const [filterDays, setFilterDays] = useState('all');
  const [selectedScan, setSelectedScan] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [playingAudio, setPlayingAudio] = useState(null);
  const audioRef = React.useRef(null);
  const { toast } = useToast();
  const ITEMS_PER_PAGE = 10;

  const fetchTranscriptions = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (filterRisk !== 'all') params.append('risk_level', filterRisk);
      if (filterDays !== 'all') params.append('days', filterDays);
      params.append('page', page.toString());
      params.append('limit', ITEMS_PER_PAGE.toString());
      
      const data = await apiCall(`/api/ai-hazard-scan/transcription-archive?${params.toString()}`);
      setTranscriptions(data.transcriptions || []);
      setTotalPages(Math.ceil((data.total || 0) / ITEMS_PER_PAGE));
    } catch (err) {
      console.error('Failed to fetch transcriptions:', err);
      setError(err.message);
      toast({ title: 'Load Failed', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filterRisk, filterDays, page, toast]);

  useEffect(() => {
    fetchTranscriptions();
  }, [fetchTranscriptions]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      fetchTranscriptions();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const getRiskBadgeClass = (risk) => {
    const classes = {
      critical: 'bg-red-100 text-red-700',
      high: 'bg-orange-100 text-orange-700',
      medium: 'bg-yellow-100 text-yellow-700',
      low: 'bg-green-100 text-green-700'
    };
    return classes[risk] || 'bg-gray-100 text-gray-700';
  };

  const highlightMatch = (text, query) => {
    if (!query || !text) return text;
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) => 
      regex.test(part) ? <mark key={i} className="bg-yellow-200 px-0.5 rounded">{part}</mark> : part
    );
  };

  const playAudio = (scan) => {
    if (playingAudio === scan.id) {
      audioRef.current?.pause();
      setPlayingAudio(null);
    } else {
      if (audioRef.current) {
        audioRef.current.src = scan.voice_note?.audio_base64;
        audioRef.current.play();
        setPlayingAudio(scan.id);
      }
    }
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileAudio className="w-5 h-5 text-purple-600" />
                Transcription Archive
              </CardTitle>
              <CardDescription>Search and browse voice note transcriptions</CardDescription>
            </div>
          </div>
          
          <Button variant="outline" size="sm" onClick={fetchTranscriptions} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        
        {/* Search and Filters */}
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search transcriptions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <Select value={filterRisk} onValueChange={(v) => { setFilterRisk(v); setPage(1); }}>
            <SelectTrigger className="w-32">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Risk" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Risks</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={filterDays} onValueChange={(v) => { setFilterDays(v); setPage(1); }}>
            <SelectTrigger className="w-32">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      
      <CardContent className="flex-1 overflow-auto">
        {error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-2" />
              <p className="text-gray-600">{error}</p>
              <Button variant="outline" className="mt-4" onClick={fetchTranscriptions}>
                Retry
              </Button>
            </div>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 text-purple-600 animate-spin" />
          </div>
        ) : transcriptions.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <FileAudio className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No transcriptions found</p>
              <p className="text-sm text-gray-400 mt-1">
                {searchQuery ? 'Try a different search query' : 'Record voice notes to see them here'}
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {transcriptions.map((scan) => (
              <div 
                key={scan.id} 
                className="border rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => setSelectedScan(scan)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium text-gray-900">{scan.scan_number}</span>
                      <Badge className={getRiskBadgeClass(scan.overall_risk)}>
                        {scan.overall_risk?.toUpperCase()}
                      </Badge>
                      {scan.voice_note && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-6 w-6 p-0"
                          onClick={(e) => { e.stopPropagation(); playAudio(scan); }}
                        >
                          {playingAudio === scan.id ? (
                            <VolumeX className="w-4 h-4 text-purple-600" />
                          ) : (
                            <Volume2 className="w-4 h-4 text-purple-600" />
                          )}
                        </Button>
                      )}
                    </div>
                    
                    <p className="text-sm text-gray-700 line-clamp-2">
                      {highlightMatch(scan.voice_transcription, searchQuery)}
                    </p>
                    
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {scan.location || 'Unknown'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(scan.created_at).toLocaleDateString()}
                      </span>
                      {scan.hazards_count > 0 && (
                        <span className="flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          {scan.hazards_count} hazard{scan.hazards_count !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <Button size="sm" variant="ghost">
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t">
            <Button 
              size="sm" 
              variant="outline" 
              disabled={page === 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-gray-600">
              Page {page} of {totalPages}
            </span>
            <Button 
              size="sm" 
              variant="outline" 
              disabled={page === totalPages}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </CardContent>
      
      {/* Detail Modal */}
      <Dialog open={!!selectedScan} onOpenChange={() => setSelectedScan(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileAudio className="w-5 h-5 text-purple-600" />
              {selectedScan?.scan_number}
            </DialogTitle>
            <DialogDescription>
              Voice transcription details
            </DialogDescription>
          </DialogHeader>
          
          {selectedScan && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge className={getRiskBadgeClass(selectedScan.overall_risk)}>
                  {selectedScan.overall_risk?.toUpperCase()}
                </Badge>
                <span className="text-sm text-gray-500">
                  {new Date(selectedScan.created_at).toLocaleString()}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Location:</span>
                  <p className="font-medium">{selectedScan.location || 'Unknown'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Project:</span>
                  <p className="font-medium">{selectedScan.project_name || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Scan Type:</span>
                  <p className="font-medium capitalize">{selectedScan.scan_type}</p>
                </div>
                <div>
                  <span className="text-gray-500">Hazards Found:</span>
                  <p className="font-medium">{selectedScan.hazards_count}</p>
                </div>
              </div>
              
              {selectedScan.voice_note && (
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-purple-800">Voice Recording</span>
                    <audio 
                      src={selectedScan.voice_note.audio_base64} 
                      controls 
                      className="h-8"
                    />
                  </div>
                </div>
              )}
              
              <div className="bg-gray-50 rounded-lg p-4">
                <span className="text-sm font-medium text-gray-700 block mb-2">Transcription</span>
                <p className="text-gray-800 whitespace-pre-wrap">
                  {selectedScan.voice_transcription || 'No transcription available'}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Hidden audio element for playback */}
      <audio 
        ref={audioRef} 
        onEnded={() => setPlayingAudio(null)}
        className="hidden"
      />
    </Card>
  );
};

export default TranscriptionArchive;
